// Página de fotos do evento
import { useState } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { ArrowLeft, Download, Heart, Share2, Search, Filter, ShoppingCart } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEvent } from "../hooks/useEvents";
import { usePhotosByEvent } from "@/photos/hooks/usePhotos";
import { toastSuccess } from "@/lib/toast-utils";

export default function EventPhotosPage() {
    const [match, params] = useRoute("/events/:eventId/photos");
    const eventId = params?.eventId;
    const [searchBib, setSearchBib] = useState("");
    const [sortBy, setSortBy] = useState("newest");
    const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
    const [, setLocation] = useLocation();

    const { data: event, isLoading: eventLoading } = useEvent(eventId!);
    const { data: photos, isLoading: photosLoading } = usePhotosByEvent(eventId!);

    const filteredPhotos = photos?.photos || [];

    const sortedPhotos = [...filteredPhotos].sort((a, b) => {
        switch (sortBy) {
            case "newest":
                return new Date((b as any).uploadedat || b.uploadedAt).getTime() - new Date((a as any).uploadedat || a.uploadedAt).getTime();
            case "oldest":
                return new Date((a as any).uploadedat || a.uploadedAt).getTime() - new Date((b as any).uploadedat || b.uploadedAt).getTime();
            case "bib":
                return 0;
            default:
                return 0;
        }
    });

    const handleDownload = (photoUrl: string, fileName: string) => {
        toastSuccess({
            title: "Download iniciado!",
            description: "O download da foto foi iniciado."
        });
    };

    const handleLike = (photoId: string) => {
        toastSuccess({
            title: "Foto curtida!",
            description: "Você curtiu esta foto."
        });
    };

    const handleShare = (photoId: string) => {
        if (navigator.share) {
            navigator.share({
                title: `Foto do evento ${event?.title}`,
                url: window.location.href + `#photo-${photoId}`
            });
        } else {
            navigator.clipboard.writeText(window.location.href + `#photo-${photoId}`);
            toastSuccess({
                title: "Link copiado!",
                description: "Link copiado para a área de transferência."
            });
        }
    };

    if (eventLoading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="h-64 bg-gray-200 rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!event) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Evento não encontrado</h1>
                    <Link to="/events">
                        <Button variant="outline">
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Voltar para Eventos
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Header do Evento */}
            <section className="bg-gradient-to-r from-primary to-teal-600 text-white py-8">
                <div className="container mx-auto px-4">
                    <Link to="/events" className="inline-flex items-center text-white/80 hover:text-white mb-4">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Voltar para Eventos
                    </Link>

                    <h1 className="text-3xl font-bold mb-2">{event.title}</h1>
                    <div className="flex flex-wrap gap-4 text-white/90">
                        <span>📅 {new Date(event.date).toLocaleDateString('pt-BR')}</span>
                        {event.location && <span>📍 {event.location}</span>}
                        <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                            {sortedPhotos.length} fotos
                        </Badge>
                    </div>
                </div>
            </section>

            {/* Filtros e Busca */}
            <section className="py-6 bg-white border-b">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row gap-4 items-center">
                        <div className="flex-1 max-w-md">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    placeholder="Buscar por número do peito..."
                                    value={searchBib}
                                    onChange={(e) => setSearchBib(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <Select value={sortBy} onValueChange={setSortBy}>
                                <SelectTrigger className="w-48">
                                    <Filter className="h-4 w-4 mr-2" />
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="newest">Mais Recentes</SelectItem>
                                    <SelectItem value="oldest">Mais Antigas</SelectItem>
                                    <SelectItem value="bib">Por Número do Peito</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            </section>

            {/* Grid de Fotos */}
            <section className="py-8">
                <div className="container mx-auto px-4">
                    {photosLoading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {Array.from({ length: 12 }).map((_, i) => (
                                <div key={i} className="aspect-square bg-gray-200 rounded-lg animate-pulse"></div>
                            ))}
                        </div>
                    ) : sortedPhotos.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="text-6xl mb-4">📸</div>
                            <h3 className="text-xl font-semibold mb-2">
                                {searchBib ? "Nenhuma foto encontrada" : "Nenhuma foto disponível"}
                            </h3>
                            <p className="text-muted-foreground">
                                {searchBib
                                    ? `Não encontramos fotos para o número ${searchBib}`
                                    : "As fotos deste evento ainda não foram publicadas"
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {sortedPhotos.map((photo) => (
                                <Card key={photo.id} className="overflow-hidden group hover:shadow-lg transition-shadow">
                                    <div className="relative aspect-square">
                                        <img
                                            src={(photo as any).thumbnailurl}
                                            alt={`Foto do evento`}
                                            className="w-full h-full object-cover cursor-pointer"
                                            onClick={() => setSelectedPhoto(photo.id)}
                                            onError={(e) => {
                                                console.error('Erro ao carregar imagem:', photo);
                                                e.currentTarget.src = 'https://via.placeholder.com/300x300?text=Imagem+Indisponível';
                                            }}
                                        />

                                        {/* Overlay com ações */}
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                onClick={() => handleLike(photo.id)}
                                                className="bg-white/90 hover:bg-white"
                                            >
                                                <Heart className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                onClick={() => handleShare(photo.id)}
                                                className="bg-white/90 hover:bg-white"
                                            >
                                                <Share2 className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                onClick={() => setLocation(`/photos/${photo.id}`)}
                                                className="bg-white/90 hover:bg-white"
                                            >
                                                <ShoppingCart className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>

                                    <CardContent className="p-3">
                                        <div className="flex justify-between items-center text-sm text-muted-foreground">
                                            <span>Foto do evento</span>
                                            <span>
                                                {new Date((photo as any).uploadedat || photo.uploadedAt).toLocaleDateString('pt-BR')}
                                            </span>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Modal de foto */}
            {selectedPhoto && (
                <div
                    className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                    onClick={() => setSelectedPhoto(null)}
                >
                    <div className="max-w-4xl max-h-full">
                        <img
                            src={(sortedPhotos.find(p => p.id === selectedPhoto) as any)?.storageurl}
                            alt="Foto ampliada"
                            className="max-w-full max-h-full object-contain"
                            onClick={(e) => e.stopPropagation()}
                            onError={(e) => {
                                console.error('Erro ao carregar imagem ampliada:', sortedPhotos.find(p => p.id === selectedPhoto));
                                e.currentTarget.src = 'https://via.placeholder.com/800x600?text=Imagem+Indisponível';
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
}

